import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@repo/atomic-components';

export interface ExpandableTableCellProps {
  children: ReactNode;
  className?: string;
  maxCollapsedHeightPx?: number;
}

export const ExpandableTableCell = ({ children, className, maxCollapsedHeightPx = 250 }: ExpandableTableCellProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const measureOverflow = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    setHasOverflow(el.scrollHeight > el.clientHeight + 1);
  }, []);

  useLayoutEffect(() => {
    measureOverflow();
    const el = contentRef.current;
    if (!el) return;

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => measureOverflow());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener('resize', measureOverflow);
    return () => window.removeEventListener('resize', measureOverflow);
  }, [children, isExpanded, measureOverflow]);

  return (
    <td className={className}>
      <div>
        <div className="relative">
          <div
            ref={contentRef}
            className={isExpanded ? undefined : 'overflow-hidden'}
            style={isExpanded ? undefined : { maxHeight: `${maxCollapsedHeightPx}px` }}
          >
            {children}
          </div>

          {!isExpanded && hasOverflow && (
            <>
              <div className="from-theme-white pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 bg-gradient-to-t to-transparent" />
              <div
                data-copy-exclude
                className="text-theme-text-grey pointer-events-none absolute right-2 bottom-1 z-20 text-sm select-none"
              >
                ...
              </div>
            </>
          )}
        </div>

        {hasOverflow && (
          <div data-copy-exclude className="mt-2 flex justify-end select-none">
            <Button
              type="button"
              link
              className="text-theme-black h-7 px-2 text-sm"
              onClick={() => setIsExpanded((v) => !v)}
            >
              {isExpanded ? 'See less' : 'See more'}
            </Button>
          </div>
        )}
      </div>
    </td>
  );
};
